// src/pages/SpeakerMode.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LiveChat from "../components/LiveChat";
import SegmentBar from "../components/SegmentBar";
import DraggableDrawer from "../helpers/DraggableDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

const API = "https://api18081113.queroapp.pl";

export default function SpeakerMode({
  // legacy – żeby działało dodawanie/rename w samym SegmentBar
  segments,
  setSegments,
  showInput,
  setShowInput,
  segmentInput,
  setSegmentInput,
}) {
  const navigate = useNavigate();

  const getLS = (k) => { try { return localStorage.getItem(k) || ""; } catch { return ""; } };
  const secretId =
    getLS("secretId") || getLS("secredId") || getLS("secret-id") || getLS("secred-id");

  const [groups, setGroups] = useState([]);     // [{ key, group_id, master, subs }]
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Active Stage State (Copied from AdminDashboard to fix Speaker Mode)
  const [activeStage, setActiveStage] = useState(null);

  /* --------- Active Stage Logic --------- */
  useEffect(() => {
    if (!secretId) return;
    let alive = true;

    const fetchActive = async () => {
      try {
        const res = await fetch(`${API}/active-stage-from-event/${secretId}?t=${Date.now()}`, { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          if (alive) setActiveStage(data.activeStage || null);
        }
      } catch (e) {
        console.error("Failed to fetch active stage", e);
      }
    };

    fetchActive();
    const interval = setInterval(fetchActive, 5000);

    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [secretId]);

  const updateActiveStage = async (newStage) => {
    // Optimistic update
    const previous = activeStage;
    setActiveStage(newStage);

    if (newStage) localStorage.setItem("ActiveStage", newStage);
    else localStorage.removeItem("ActiveStage");

    try {
      await fetch(`${API}/set-active-stage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, activeStage: newStage }),
      });
    } catch (e) {
      console.error("Failed to set active stage", e);
    }
  };

  useEffect(() => {
    const arrFrom = (x) =>
      Array.isArray(x) ? x : (x?.segments || x?.data || x?.items || []);

    const normalizeRow = (r) => ({
      fullName: r?.segment_name ?? r?.name ?? r?.segment ?? "",
      group_id:
        r?.group_id === 0 || r?.group_id
          ? String(r.group_id)
          : (r?.groupid ? String(r.groupid) : null),
    });

    const fetchAll = async () => {
      if (!secretId) return navigate("/");

      setLoadingGroups(true);
      try {
        // KLUCZ: dokładnie jak w AdminDashboard
        const [mastersRes, subsRes] = await Promise.all([
          fetch(`${API}/segments-with-master/${encodeURIComponent(secretId)}`),
          fetch(`${API}/segments-without-master/${encodeURIComponent(secretId)}`),
        ]);

        const mastersRaw = await mastersRes.json().catch(() => ({}));
        const subsRaw = await subsRes.json().catch(() => ({}));

        const masters = arrFrom(mastersRaw)
          .map(normalizeRow)
          .filter((x) => x.fullName);

        const subs = arrFrom(subsRaw)
          .map(normalizeRow)
          .filter((x) => x.fullName);

        // budowa mapy po group_id (tak samo jak w AdminDashboard)
        const map = new Map();
        masters.forEach((m) => {
          const gid =
            m.group_id && m.group_id !== "null" && m.group_id !== "undefined"
              ? m.group_id
              : `__nogroup__:${m.fullName}`; // stabilny key nawet bez id
          if (!map.has(gid)) map.set(gid, { key: gid, group_id: m.group_id, master: m.fullName, subs: [] });
          else map.set(gid, { ...map.get(gid), master: m.fullName });
        });

        subs.forEach((s) => {
          const gid = s.group_id ? String(s.group_id) : null;
          if (!gid) return; // bez group_id nie wiemy gdzie wpiąć
          if (map.has(gid)) {
            map.get(gid).subs.push(s.fullName);
          }
        });

        const grouped = Array.from(map.values()).filter((g) => !!g.master);
        setGroups(grouped);

        // FIX: Używamy pełnych nazw ze zbioru flattened, aby UI wyświetlało nazwy (z mapowania) a nie ID
        const flat = [...masters, ...subs].map((x) => x.fullName);
        setSegments(flat);

      } catch (err) {
        console.error("SpeakerMode: błąd pobierania grup:", err);
        setGroups([]);
        setSegments([]);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchAll();
  }, [navigate, setSegments, secretId]);

  return (
    <div className="container-fluid d-flex flex-column px-0" style={{ overflow: "hidden", position: "relative", height: "100vh" }}>
      <div className="top-bar-admin d-flex justify-content-between align-items-center mb-4">
        <button
          className="btn-gradient-secondary d-flex align-items-center justify-content-center"
          onClick={() => navigate("/admin-panel")}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> <span></span>Wróć
        </button>
        <h4 className="event-title">Tryb Prelegenta</h4>
        <div></div>
      </div>

      <div
        className="container bg-material d-flex align-items-center flex-column gy-4 mb-5"
        style={{ flex: 1, overflow: "hidden" }}
      >
        <div className="col-12 w-100" style={{ flex: 1, overflowY: "hidden" }}>
          <LiveChat activeStage={activeStage} />
        </div>
      </div>

      {/* Drawer z SegmentBar */}
      <DraggableDrawer>
        <SegmentBar
          // kluczowe: zgrupowane dane jak w AdminDashboard
          groups={groups}
          loadingGroups={loadingGroups}

          // State Lift props
          activeStage={activeStage}
          onToggleStage={updateActiveStage}

          // legacy props (tworzenie/rename)
          segments={segments}
          setSegments={setSegments}
          showInput={showInput}
          setShowInput={setShowInput}
          segmentInput={segmentInput}
          setSegmentInput={setSegmentInput}
        />
      </DraggableDrawer>
    </div>
  );
}
